'use client';

import { useEffect, useEffectEvent, useRef, useState } from 'react';

import {
  useApolloClient,
  useMutation,
  useSubscription,
} from '@apollo/client/react';
import { useSearchParams } from 'next/navigation';

import { navigateTopLevel, RemoteWorkerScriptError } from '@/shared-utilities';
import {
  CREATE_MOBILE_AUDIO_UPLOAD_URL,
  GET_ASSEMBLYAI_TOKEN,
  SUBMIT_MOBILE_VOICE_TRANSCRIPT,
  SYNC_SESSION_TIMESTAMP,
  UT_SESSION_STATUS,
  UtSessionStatusType,
} from '@/survey-graphql';

import { createHyperbeamSessionGateway } from './shared-survey-hyperbeam-session.gateway';
import {
  createMobileUtSession,
  type MobileUtSession,
} from './shared-survey-mobile-ut-phone-session.helpers';
import MobileUtPhoneStatus from './shared-survey-mobile-ut-phone-status';
import { VOICE_CONNECTION_ERROR_MESSAGE } from './shared-survey-mobile-ut-phone.consts';
import {
  useMobileUtPhoneActivitySignal,
  useMobileUtPhoneUnloadGuard,
} from './shared-survey-mobile-ut-phone.hooks';
import type { MobileUtPhonePhase } from './shared-survey-mobile-ut-phone.types';
import {
  buildHyperbeamStartUrl,
  clampHyperbeamDimensions,
  generateMobileUtUniqueId,
  getMobileUtScreenSize,
  parseMobileUtEndRedirectUrl,
} from './shared-survey-mobile-ut.helpers';

interface Props {
  projectId: string;
  sessionId?: string | null;
  screenerSessionKey?: string | null;
  title: string;
  usesMicrophone?: boolean;
}

export default function MobileUtPhoneShell({
  projectId,
  sessionId = null,
  screenerSessionKey = null,
  title,
  usesMicrophone = true,
}: Props) {
  const searchParams = useSearchParams();

  const containerRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<MobileUtSession | null>(null);

  const [phase, setPhase] = useState<MobileUtPhonePhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [completionRedirectUrl, setCompletionRedirectUrl] = useState<
    string | null
  >(null);
  const [uniqueId] = useState(generateMobileUtUniqueId);

  const client = useApolloClient();

  const [getAssemblyAIToken] = useMutation(GET_ASSEMBLYAI_TOKEN);
  const [submitTranscript] = useMutation(SUBMIT_MOBILE_VOICE_TRANSCRIPT);
  const [createUploadUrl] = useMutation(CREATE_MOBILE_AUDIO_UPLOAD_URL);
  const [syncTimestamp] = useMutation(SYNC_SESSION_TIMESTAMP);

  // BE replays last terminal status on (re)subscribe within 2h; session.end() guards against duplicate processing on cached-replay race.
  useSubscription(UT_SESSION_STATUS, {
    variables: { uniqueId },
    skip: phase !== 'active',
    onData: ({ data: { data } }) => {
      const payload = data?.utSessionStatus;
      if (payload?.status !== UtSessionStatusType.SessionEnded) return;
      if (!sessionRef.current?.end()) return;

      setCompletionRedirectUrl(parseMobileUtEndRedirectUrl(payload.reason));
      setPhase('ended');
    },
  });

  useMobileUtPhoneUnloadGuard(phase, sessionRef);
  useMobileUtPhoneActivitySignal(phase);

  const disposeSession = useEffectEvent(() => {
    sessionRef.current?.dispose();
    sessionRef.current = null;
  });

  useEffect(() => () => disposeSession(), []);

  const screenSize = getMobileUtScreenSize(searchParams);

  function handleVoiceFailure() {
    setErrorMessage(VOICE_CONNECTION_ERROR_MESSAGE);
    setPhase('error');
  }

  function getSession() {
    sessionRef.current ??= createMobileUtSession({
      uniqueId,
      mutations: {
        getAssemblyAIToken,
        submitTranscript,
        createUploadUrl,
        syncTimestamp,
      },
      hyperbeamGateway: createHyperbeamSessionGateway(client),
      onVoiceFailure: handleVoiceFailure,
    });
    return sessionRef.current;
  }

  async function handleStartTest() {
    if (!containerRef.current) return;

    setPhase('starting');
    setErrorMessage(null);

    try {
      const startUrl = buildHyperbeamStartUrl({
        projectId,
        uniqueId,
        sessionId,
        screenerSessionKey,
        forwardedParams: searchParams,
      });

      // Use screen size from URL params (set by phone in startUrl) if available,
      // otherwise fall back to current window dimensions (phone creating the session)
      const { width, height } =
        screenSize ??
        clampHyperbeamDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });

      const isLive = await getSession().start({
        container: containerRef.current,
        usesMicrophone,
        startUrl,
        width,
        height,
      });

      if (isLive) setPhase('active');
    } catch (error) {
      if (error instanceof RemoteWorkerScriptError) {
        console.error('[mobile-ut] voice worker script unavailable', error);
        setErrorMessage(VOICE_CONNECTION_ERROR_MESSAGE);
      } else {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to start test session'
        );
      }
      setPhase('error');
    }
  }

  return (
    <div className='relative h-dvh w-screen overflow-hidden bg-gray-950'>
      {/* Hyperbeam container — always in DOM for ref availability */}
      <div
        ref={containerRef}
        data-test='mobile-ut-hyperbeam-container'
        className='invisible absolute inset-0 size-full data-[active=true]:visible'
        data-active={phase === 'active'}
      />

      <MobileUtPhoneStatus
        phase={phase}
        title={title}
        usesMicrophone={usesMicrophone}
        errorMessage={errorMessage}
        completionRedirectUrl={completionRedirectUrl}
        onStart={handleStartTest}
        onContinue={navigateTopLevel}
      />
    </div>
  );
}
